import React, { useEffect, useRef, useState } from 'react';
import { Maximize2, RotateCw, ZoomIn, ZoomOut, Layers, Play, Pause, CalendarCheck } from 'lucide-react';
import RegistrationMark from './RegistrationMark';
import QuoteRequestDialog from './QuoteRequestDialog';
import BrandLogo from './BrandLogo';

const BARBIERI_PRODUCT = { id: 'spectro-lfp-qb-textile', name: 'Spectro LFP qb Textile Edition' };

const FRAMES = [
  { deg: 0, label: 'Front', url: '/barbieri/frame_000.webp' },
  { deg: 45, label: 'Front · Right', url: '/barbieri/frame_045.webp' },
  { deg: 90, label: 'Right side', url: '/barbieri/frame_090.webp' },
  { deg: 135, label: 'Back · Right', url: '/barbieri/frame_135.webp' },
  { deg: 180, label: 'Back', url: '/barbieri/frame_180.webp' },
  { deg: 225, label: 'Back · Left', url: '/barbieri/frame_225.webp' },
  { deg: 270, label: 'Left side', url: '/barbieri/frame_270.webp' },
  { deg: 315, label: 'Front · Left', url: '/barbieri/frame_315.webp' },
];

const SPECS = [
  { label: 'Accuracy', value: 'ΔE < 0.3' },
  { label: 'Aperture', value: '2 / 6 / 8 mm' },
  { label: 'Modes', value: 'M0 · M1 · M2 · M3' },
];

const AngleSelector = ({ frameIdx, onSelect }) => (
  <div className="mt-7 grid grid-cols-4 gap-2" data-testid="spectro-angle-selector">
    {FRAMES.map((f, i) => (
      <button
        key={f.deg}
        data-testid={`spectro-angle-btn-${f.deg}`}
        onClick={() => onSelect(f.deg)}
        className={`p-2.5 rounded-xl border-2 btn-transition text-center ${
          frameIdx === i
            ? 'border-[#0A2A63] bg-[#0A2A63] text-white shadow-lg'
            : 'border-slate-200 hover:border-[#0A2A63] bg-white text-[#050F2E]'
        }`}
      >
        <p className="text-sm font-black">{f.deg}°</p>
        <p className="text-[9px] uppercase tracking-wide opacity-70 font-bold leading-tight">{f.label}</p>
      </button>
    ))}
  </div>
);

const ViewerControlButton = ({ testId, onClick, label, active, children }) => (
  <button
    data-testid={testId}
    className={`h-10 w-10 rounded-full shadow-md flex items-center justify-center transition-colors ${
      active ? 'bg-[#FF4E50] text-white' : 'bg-white hover:bg-[#0A2A63] hover:text-white'
    }`}
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    onMouseDown={(e) => e.stopPropagation()}
    aria-label={label}
  >
    {children}
  </button>
);

const ViewerControls = ({ onZoomIn, onZoomOut, autoRotate, onToggleAuto, onReset }) => (
  <div className="absolute bottom-5 right-5 flex flex-col gap-2">
    <ViewerControlButton testId="spectro-zoom-in-btn" onClick={onZoomIn} label="Zoom in">
      <ZoomIn className="h-4 w-4" />
    </ViewerControlButton>
    <ViewerControlButton testId="spectro-zoom-out-btn" onClick={onZoomOut} label="Zoom out">
      <ZoomOut className="h-4 w-4" />
    </ViewerControlButton>
    <ViewerControlButton testId="spectro-autorotate-btn" onClick={onToggleAuto} label="Toggle auto-rotate" active={autoRotate}>
      {autoRotate ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
    </ViewerControlButton>
    <ViewerControlButton testId="spectro-reset-btn" onClick={onReset} label="Reset view">
      <Maximize2 className="h-4 w-4" />
    </ViewerControlButton>
  </div>
);

const FrameStack = ({ frameIdx, tilt, zoom }) => (
  <div
    className="absolute inset-0 flex items-center justify-center pointer-events-none"
    style={{
      transform: `rotateX(${-tilt}deg) scale(${zoom})`,
      transformStyle: 'preserve-3d',
      transition: 'transform 0.15s ease-out',
    }}
  >
    {FRAMES.map((f, i) => (
      <img
        key={f.deg}
        src={f.url}
        alt={`Barbieri Spectro LFP qb — ${f.label}`}
        draggable="false"
        className="absolute max-h-[440px] max-w-[86%] object-contain drop-shadow-2xl"
        style={{ opacity: frameIdx === i ? 1 : 0, transition: 'opacity 0.35s ease' }}
      />
    ))}
    {/* Ground shadow */}
    <div className="absolute left-1/2 -translate-x-1/2 bottom-16 h-10 w-2/3 rounded-[50%] blur-xl bg-[#0A2A63]/15" />
  </div>
);

const useDragRotation = (angle, setAngle, tilt, setTilt, setAutoRotate) => {
  const dragRef = useRef({ dragging: false, sx: 0, sy: 0, a: 0, t: 0 });

  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current.dragging) return;
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const cy = e.touches ? e.touches[0].clientY : e.clientY;
      setAngle(dragRef.current.a + (cx - dragRef.current.sx) * 0.45);
      setTilt(Math.max(-14, Math.min(14, dragRef.current.t + (cy - dragRef.current.sy) * 0.15)));
    };
    const onUp = () => { dragRef.current.dragging = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove);
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [setAngle, setTilt]);

  const onDown = (e) => {
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    dragRef.current = { dragging: true, sx: cx, sy: cy, a: angle, t: tilt };
    setAutoRotate(false);
  };

  return { dragRef, onDown };
};

const SpectroViewer3D = () => {
  const [angle, setAngle] = useState(45);
  const [tilt, setTilt] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [autoRotate, setAutoRotate] = useState(true);
  const { dragRef, onDown } = useDragRotation(angle, setAngle, tilt, setTilt, setAutoRotate);

  const norm = ((angle % 360) + 360) % 360;
  const frameIdx = Math.round(norm / 45) % 8;

  useEffect(() => {
    FRAMES.forEach((f) => { const img = new Image(); img.src = f.url; });
  }, []);

  useEffect(() => {
    if (!autoRotate) return;
    const t = setInterval(() => {
      if (!dragRef.current.dragging) setAngle((a) => a + 45);
    }, 2600);
    return () => clearInterval(t);
  }, [autoRotate, dragRef]);

  return (
    <section className="py-28 bg-white relative overflow-hidden" data-testid="spectro-3d-section">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 80% 20%, rgba(0,183,199,0.08), transparent 50%),' +
            'radial-gradient(circle at 15% 85%, rgba(245,158,11,0.08), transparent 50%)',
        }}
      />
      <div className="container-pn relative">
        <div className="grid md:grid-cols-12 gap-12 items-center">
          {/* Left: Copy + controls */}
          <div className="md:col-span-5 order-2 md:order-1">
            <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3 inline-flex items-center gap-2">
              <Layers className="h-3.5 w-3.5" /> Interactive 360° Viewer
            </p>
            <BrandLogo brand="barbieri" imgClassName="h-8" className="mb-4" testId="spectro-barbieri-logo" />
            <h2 className="text-4xl md:text-5xl font-black text-[#050F2E] tracking-tight leading-tight">
              Spectro LFP qb{' '}
              <span className="pn-gradient-text">
                TextileED
              </span>
            </h2>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              Drag to spin the reference spectrophotometer for textile printing.
              Explore it from every angle — automated measurement head, quick-bridge table and camera-assisted positioning.
            </p>

            <AngleSelector frameIdx={frameIdx} onSelect={(deg) => { setAutoRotate(false); setAngle(deg); }} />

            {/* Specs */}
            <div className="mt-7 grid grid-cols-3 gap-4">
              {SPECS.map((s) => (
                <div key={s.label} className="bg-slate-50 rounded-xl p-4">
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{s.label}</p>
                  <p className="text-lg font-black text-[#0A2A63]">{s.value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: 360 Viewer */}
          <div className="md:col-span-7 order-1 md:order-2">
            <div
              className="relative h-[500px] md:h-[600px] rounded-3xl bg-gradient-to-br from-slate-50 via-white to-slate-100 border border-slate-200 shadow-2xl overflow-hidden cursor-grab active:cursor-grabbing select-none touch-none"
              onMouseDown={onDown}
              onTouchStart={onDown}
              data-testid="spectro-3d-viewport"
              style={{ perspective: '1400px' }}
            >
              <RegistrationMark className="absolute top-4 left-4 opacity-40" size={22} />
              <RegistrationMark className="absolute top-4 right-4 opacity-40" size={22} />

              {/* Decorative rings */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="h-[420px] w-[420px] rounded-full border-2 border-dashed border-[#FF4E50]/30 animate-spin-slow" />
                <div className="absolute h-[300px] w-[300px] rounded-full border border-[#0A2A63]/10" />
                <div className="absolute h-[190px] w-[190px] rounded-full bg-[#00B7C7]/5" />
              </div>

              <FrameStack frameIdx={frameIdx} tilt={tilt} zoom={zoom} />

              {/* Hint */}
              <div className="absolute top-5 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 bg-white/85 backdrop-blur-sm rounded-full px-4 py-2 text-xs font-semibold text-[#0A2A63] shadow-sm pointer-events-none">
                <RotateCw className="h-3.5 w-3.5" /> Drag to rotate 360°
              </div>

              {/* Request a demo CTA */}
              <div
                className="absolute top-5 right-16"
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
              >
                <QuoteRequestDialog product={BARBIERI_PRODUCT}>
                  <button
                    data-testid="spectro-request-demo-btn"
                    className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-black uppercase tracking-widest text-[#050F2E] shadow-lg bg-gradient-to-r from-[#FFD700] to-[#FF4E50] hover:shadow-xl hover:-translate-y-0.5 btn-transition"
                  >
                    <CalendarCheck className="h-4 w-4" /> Request a demo
                  </button>
                </QuoteRequestDialog>
              </div>

              {/* Angle readout */}
              <div className="absolute bottom-5 left-5 bg-[#0A2A63] text-white rounded-2xl px-5 py-3 shadow-lg pointer-events-none" data-testid="spectro-angle-readout">
                <p className="text-[10px] uppercase tracking-widest opacity-70 font-bold">Viewing angle</p>
                <p className="text-xl font-black font-mono">{FRAMES[frameIdx].deg}° <span className="text-xs font-bold opacity-70">· {FRAMES[frameIdx].label}</span></p>
              </div>

              {/* Compass dots */}
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-1.5 pointer-events-none">
                {FRAMES.map((f, i) => (
                  <span
                    key={f.deg}
                    className="rounded-full transition-all duration-300"
                    style={{
                      height: 6,
                      width: frameIdx === i ? 20 : 6,
                      backgroundColor: frameIdx === i ? '#FF4E50' : 'rgba(10,37,64,0.25)',
                    }}
                  />
                ))}
              </div>

              <ViewerControls
                onZoomIn={() => setZoom((z) => Math.min(1.6, z + 0.15))}
                onZoomOut={() => setZoom((z) => Math.max(0.7, z - 0.15))}
                autoRotate={autoRotate}
                onToggleAuto={() => setAutoRotate((v) => !v)}
                onReset={() => { setAngle(45); setTilt(0); setZoom(1); }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SpectroViewer3D;
